import React from "react";
import {
  Box,
  Text,
  Heading,
  VStack,
  HStack,
  Button,
  Stack,
  useColorMode,
  useBreakpointValue,
} from "@chakra-ui/react";
import type { StackDirection } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

interface AuthorProps {
  author: {
    id?: string;
    fullName: string;
    firstName?: string | null;
    lastName?: string | null;
    birthYear?: number | null;
    deathYear?: number | null;
    bio?: string | null;
    language?: string;
  };
  onChange?: () => void;
}

const Author: React.FC<AuthorProps> = ({ author, onChange }) => {
  const { colorMode } = useColorMode();
  const { t } = useTranslation();

  // Image size similar to ReadBook
  const imageSize = useBreakpointValue({
    base: { width: "120px", height: "180px" },
    md: { width: "150px", height: "225px" },
    lg: { width: "180px", height: "270px" },
  }) || { width: "150px", height: "225px" };

  const headerLayout = useBreakpointValue<StackDirection>({
    base: "column",
    md: "row",
  });

  // Format birth/death year as "DOB - DOD"
  const formatYears = () => {
    if (author.birthYear && author.deathYear) {
      return `${author.birthYear} - ${author.deathYear}`;
    } else if (author.birthYear) {
      return `${author.birthYear}`;
    } else if (author.deathYear) {
      return `- ${author.deathYear}`;
    }
    return null;
  };

  const yearsText = formatYears();

  return (
    <Box
      id="author-display-container"
      p={{ base: 4, md: 4 }}
      borderRadius="md"
      bg={
        colorMode === "light"
          ? "rgba(240, 240, 240, 0.6)"
          : "rgba(30, 35, 45, 0.6)"
      }
      boxShadow="sm"
      position="relative"
    >
      <Stack
        id="author-display-stack"
        direction={headerLayout}
        spacing={{ base: 4, md: 8 }}
        align={{ base: "center", md: "flex-start" }}
      >
        {/* Author Image Placeholder */}
        <Box
          id="author-image-container"
          width={imageSize.width}
          textAlign={{ base: "center", md: "left" }}
          position="relative"
          flexShrink={0}
        >
          <Box
            width={imageSize.width}
            height={imageSize.height}
            borderRadius="md"
            bg={colorMode === "light" ? "gray.200" : "gray.700"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxShadow="sm"
            mb={2}
          >
            <Text
              fontSize="4xl"
              color={colorMode === "light" ? "gray.400" : "gray.500"}
            >
              {author.fullName.charAt(0).toUpperCase()}
            </Text>
          </Box>
          {yearsText && (
            <Text
              id="author-years"
              fontSize="md"
              fontWeight="medium"
              textAlign="center"
              color={colorMode === "light" ? "gray.700" : "gray.300"}
            >
              {yearsText}
            </Text>
          )}
        </Box>

        {/* Author Info */}
        <VStack
          id="author-info-stack"
          align="flex-start"
          spacing={4}
          width="100%"
        >
          <Heading
            id="author-name"
            size="lg"
            textAlign={{ base: "center", md: "left" }}
            width="100%"
            color={colorMode === "light" ? "gray.800" : "white"}
          >
            {author.fullName}
          </Heading>

          {/* Biography - All on one line, more prominent */}
          {author.bio && (
            <Box width="100%">
              <Text
                id="author-bio"
                fontSize="lg"
                fontWeight="normal"
                color={colorMode === "light" ? "gray.700" : "gray.200"}
                lineHeight="1.6"
              >
                {author.bio}
              </Text>
            </Box>
          )}
        </VStack>
      </Stack>

      {/* Change button - responsive positioning */}
      {onChange && (
        <Box
          position={{ base: "static", md: "absolute" }}
          bottom={{ base: "auto", md: 4 }}
          right={{ base: "auto", md: 4 }}
          mt={{ base: 4, md: 0 }}
          textAlign={{ base: "center", md: "right" }}
        >
          <Button
            id="change-author-button"
            size="sm"
            variant="ghost"
            onClick={onChange}
          >
            {t("createBook.author.change", "Change")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Author;
